<template>
  <div class="uberall-search-interface__listing-wrapper">
    <div class="uberall-search-interface__search-bar-wrapper">
      <h1
        class="uberall-search-interface__search-title text-heading-m"
        v-text="$string('store_locator.title')"
      />

      <div class="uberall-search-interface__input-wrapper">
        <label
          for="uberall-search-interface-input"
          class="visually-hidden"
          v-text="$string('predictive_search.placeholder')"
        />

        <icon-search v-if="!isMobile" />
        <icon-search-mobile v-if="isMobile" />

        <input
          id="uberall-search-interface-input"
          v-model="inputValue"
          type="search"
          :placeholder="$string('store_locator.search_placeholder')"
          class="uberall-search-interface__input text-body-2xs-regular"
          autocomplete="off"
          name="q"
          required
        >

        <button
          type="button"
          class="uberall-search-interface__input-clear"
          :class="{ 'hidden': inputValue === '' }"
          @click="clearInputField"
        >
          <icon-close />
        </button>
      </div>

      <div class="uberall-search-interface__footer-wrapper">
        <p
          class="uberall-search-interface__locations-count text-body-2xs-regular"
          v-text="`${storeListingArray.length} locations`"
        />

        <btn
          class="uberall-search-interface__view-all"
          class-label="text-body-2xs-regular"
          :label="$string('store_locator.view_all')"
          :disabled="storeListingArray.length === locations.length"
          modifiers="text center"
          @click="$emit('reset-view')"
        />
      </div>
    </div>

    <div
      v-if="isMobile"
      id="uberall-locator-map"
      class="store-locator-map__uberall-locator-map"
    />

    <div class="uberall-search-interface__search-listing-wrapper">
      <div class="uberall-search-interface__search-items">
        <template
          v-for="(location, index) of storeListingArray"
          :key="index"
        >
          <uberall-search-line-item
            :store-data="location"
            @location-id="emitListSelectedStoreOnMap"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>

import { mapState } from 'vuex'
import Btn from '~global/btn/btn'
import IconSearch from '~icons/general/search.svg'
import IconSearchMobile from '~icons/general/search-mobile.svg'
import UberallSearchLineItem from '../uberall-search-line-item/uberall-search-line-item.vue'
import IconClose from '~icons/directional-navigation/close-outline.svg'
import { calculateFlatDistance, getUserGeolocation, storeLocatorProps } from '../helpers'

export default {
  name: 'UberallSearchInterface',

  components: {
    Btn,
    IconSearch,
    IconSearchMobile,
    UberallSearchLineItem,
    IconClose,
  },

  props: {
    visibleStores: {
      type: Array,
      default: () => null,
    },
  },

  emits: ['locations-data', 'location-id', 'address-search', 'reset-view', 'user-location'],

  data() {
    return {
      locations: [],
      inputValue: '',
      userGeolocation: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Filter stores visible on map
     */
    storeListingArray() {
      if (!this.visibleStores) {
        return this.locations
      }

      return this.locations.filter((location) => this.visibleStores.includes(location.id))
    },
  },

  watch: {
    userGeolocation() {
      if (this.userGeolocation && this.userGeolocation?.lat && this.locations.length) {
        this.locations = this.sortStoresPerGeolocation(this.locations)

        this.$emit('locations-data', this.locations)
        this.$emit('user-location', this.userGeolocation)
      }
    },

    inputValue() {
      if (this.inputValue !== '') {
        this.$emit('address-search', this.inputValue)
      }
    },
  },

  mounted() {
    this.getLocations()
    this.setUserGeolocation()
  },

  methods: {

    /**
     * Get location from Uberall
     */
    async getLocations() {
      const options = {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      }
      const locationResults = await fetch(
        `${storeLocatorProps.activeEnvironmentUrl}all${storeLocatorProps.landingPageStoreFields}`,
        options,
      ).then(response => response.json())

      this.locations = !this.userGeolocation || !this.userGeolocation.lat
        ? this.sortStoresAlphabetically(locationResults?.response?.locations)
        : this.sortStoresPerGeolocation(locationResults?.response?.locations)

      this.$emit('locations-data', this.locations)
      this.getLocationServices()
    },

    /**
     * Get store services
     */
    async getLocationServices() {
      const storeIds = this.locations
        .map((location) => location.id)

      if (!storeIds.length) {
        return
      }

      await this.sendRequestsInBatches(storeIds, 5, 1500)
    },

    /**
     * Get store services via batches
     * @param storeIds
     * @param batchSize
     * @param delay
     */
    async sendRequestsInBatches(storeIds, batchSize, delay) {
      for (let index = 0; index < storeIds.length; index += batchSize) {
        const batch = storeIds.slice(index, index + batchSize)
        const promises = batch
          .map((storeId) => fetch(`${storeLocatorProps.activeEnvironmentUrl}${storeId}${storeLocatorProps.landingPageServiceField}`)
            .then(response => response.json()))
        // eslint-disable-next-line no-await-in-loop
        const services = await Promise.all(promises)

        this.updateLocationsData(services)

        if (index + batchSize < storeIds.length) {
          // eslint-disable-next-line no-await-in-loop
          await this.sleep(delay)
        }
      }
    },

    /**
     * Wait time for batch
     * @param waitTime
     */
    sleep(waitTime) {
      return new Promise((resolve) => {
        setTimeout(resolve, waitTime)
      })
    },

    /**
     * Update location data when data received
     * @param services
     */
    updateLocationsData(services) {
      const servicesMapped = services.map((service) => {
        return {
          id: service?.response?.id,
          services: service?.response?.services,
        }
      })

      this.locations = this.locations.map((oldLocation) => {
        const foundService = servicesMapped.find((service) => {
          return service.id === oldLocation.id && service?.services?.length > 0
        })

        if (foundService) {
          return {
            ...oldLocation,
            services: foundService?.services,
          }
        }

        return oldLocation
      })
    },

    /**
     * Emits id of selected store from search list
     * @param id
     */
    emitListSelectedStoreOnMap(id) {
      this.$emit('location-id', id)
    },

    /**
     * Get user geolocation
     */
    async setUserGeolocation() {
      this.userGeolocation = await getUserGeolocation()
    },

    /**
     * Sort locations alphabetically
     * @param locations
     */
    sortStoresAlphabetically(locations) {
      return locations.sort((prevStore, nextStore) => {
        if (prevStore.name < nextStore.name) {
          return -1
        }

        if (prevStore.name > nextStore.name) {
          return 1
        }

        return 0
      })
    },

    /**
     * Sort locations closest to the user
     * @param locations
     */
    sortStoresPerGeolocation(locations) {
      const locationsDistanceAdded = locations.map((location) => {
        return {
          ...location,
          distance: calculateFlatDistance(
            this.userGeolocation,
            { lat: location.lat, lng: location.lng },
          ),
        }
      })

      return locationsDistanceAdded.sort((prevStore, nextStore) => {
        return prevStore.distance - nextStore.distance
      })
    },

    /**
     * Empty input field
     */
    clearInputField() {
      this.inputValue = ''
      this.$emit('reset-view')
    },

  },
}
</script>
